import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './locales/en';
import it from './locales/it';
import de from './locales/de';
import fr from './locales/fr';
import es from './locales/es';

/**
 * Supported app languages.
 * To add a new language:
 *  1. add the locale code below
 *  2. create src/i18n/locales/<code>.ts that default-exports a Dictionary
 *  3. register it in `dictionaries` below
 *  4. add an entry in LANGUAGE_OPTIONS (label + intlLocale)
 */
export type Lang = 'it' | 'en' | 'de' | 'fr' | 'es';
export type Dictionary = Record<string, string>;

const dictionaries: Record<Lang, Dictionary> = { it, en, de, fr, es };

/**
 * Display + locale metadata for each supported language.
 * `intlLocale` is what we pass to Intl.* APIs and date-fns
 * so dates / numbers / currencies follow the user's choice.
 */
export const LANGUAGE_OPTIONS: ReadonlyArray<{
  code: Lang;
  label: string;
  shortLabel: string;
  intlLocale: string;
  flag: string;
}> = [
  { code: 'it', label: 'Italiano', shortLabel: 'IT', intlLocale: 'it-IT', flag: '🇮🇹' },
  { code: 'en', label: 'English', shortLabel: 'EN', intlLocale: 'en-US', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', shortLabel: 'DE', intlLocale: 'de-DE', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', shortLabel: 'FR', intlLocale: 'fr-FR', flag: '🇫🇷' },
  { code: 'es', label: 'Español', shortLabel: 'ES', intlLocale: 'es-ES', flag: '🇪🇸' },
];

const LANG_STORAGE_KEY = 'app_lang';
const SUPPORTED: ReadonlySet<Lang> = new Set(LANGUAGE_OPTIONS.map(o => o.code));

function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && SUPPORTED.has(value as Lang);
}

function detectInitialLang(defaultLang: Lang): Lang {
  if (typeof window === 'undefined') return defaultLang;
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    // localStorage may be unavailable (private mode / quota)
  }
  try {
    const nav = (window.navigator?.language || '').slice(0, 2).toLowerCase();
    if (isLang(nav)) return nav;
  } catch {
    // navigator unavailable
  }
  return defaultLang;
}

/**
 * Simple {{var}} interpolation. Missing values render as empty string.
 */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key) => {
    const v = vars[key];
    return v === undefined || v === null ? '' : String(v);
  });
}

export interface I18nContextValue {
  /** Current language code. */
  lang: Lang;
  /** Locale string usable with Intl.* APIs (e.g. 'it-IT'). */
  locale: string;
  /** Change the active language (persists to localStorage). */
  setLang: (l: Lang) => void;
  /** Translate `key`. Optional `fallback` is used if the key is missing in BOTH the active language and EN. */
  t: (key: string, fallback?: string, vars?: Record<string, string | number>) => string;
  /** Translate with interpolation only (shortcut for t(key, undefined, vars)). */
  tx: (key: string, vars: Record<string, string | number>) => string;
  /** Format a Date / ms / ISO string in the active locale. */
  formatDate: (
    value: Date | number | string,
    options?: Intl.DateTimeFormatOptions,
  ) => string;
  /** Format a number in the active locale. */
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** Format a currency amount in the active locale. Defaults to EUR. */
  formatCurrency: (
    value: number,
    currency?: string,
    options?: Intl.NumberFormatOptions,
  ) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({
  children,
  defaultLang = 'it',
}: {
  children: React.ReactNode;
  defaultLang?: Lang;
}) {
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang(defaultLang));

  // Persist on every change (and on first mount if storage was empty).
  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    // Keep <html lang="..."> in sync so screen readers / SEO behave correctly.
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    if (!isLang(l)) return;
    setLangState(l);
  }, []);

  const locale = useMemo(
    () => LANGUAGE_OPTIONS.find(o => o.code === lang)?.intlLocale ?? 'it-IT',
    [lang],
  );

  const t = useCallback(
    (key: string, fallback?: string, vars?: Record<string, string | number>): string => {
      const primary = dictionaries[lang]?.[key];
      if (primary !== undefined) return interpolate(primary, vars);
      // Smart fallback: try EN before showing fallback or key.
      const fallbackEn = lang !== 'en' ? dictionaries.en?.[key] : undefined;
      if (fallbackEn !== undefined) return interpolate(fallbackEn, vars);
      if (fallback !== undefined) return interpolate(fallback, vars);
      return key;
    },
    [lang],
  );

  const tx = useCallback(
    (key: string, vars: Record<string, string | number>) => t(key, undefined, vars),
    [t],
  );

  const formatDate = useCallback(
    (value: Date | number | string, options?: Intl.DateTimeFormatOptions) => {
      const d = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      try {
        return new Intl.DateTimeFormat(locale, options ?? { dateStyle: 'medium' }).format(d);
      } catch {
        return d.toLocaleDateString(locale);
      }
    },
    [locale],
  );

  const formatNumber = useCallback(
    (value: number, options?: Intl.NumberFormatOptions) => {
      try {
        return new Intl.NumberFormat(locale, options).format(value);
      } catch {
        return String(value);
      }
    },
    [locale],
  );

  const formatCurrency = useCallback(
    (value: number, currency: string = 'EUR', options?: Intl.NumberFormatOptions) => {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          ...options,
        }).format(value);
      } catch {
        return `${value} ${currency}`;
      }
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ lang, locale, setLang, t, tx, formatDate, formatNumber, formatCurrency }),
    [lang, locale, setLang, t, tx, formatDate, formatNumber, formatCurrency],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
